import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { modificheApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ImageLightbox } from '../components/ImageLightbox';
import { Settings2, Plus, Trash2, Loader2, Pencil, ZoomIn } from 'lucide-react';
import { toast } from 'sonner';
import { FotoUploader } from '../components/FotoUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIE_MODIFICHE = [
  'Estetica',
  'Performance',
  'Comfort',
  'Sicurezza',
  'Elettronica',
  'Scarico',
  'Sospensioni',
  'Altro'
];

export default function Modifiche() {
  const { motoList, selectedMotoId } = useAuth();
  const [modifiche, setModifiche] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [formData, setFormData] = useState({
    moto_id: '',
    titolo: '',
    descrizione: '',
    categoria: '',
    costo: null,
    data: '',
    foto_urls: [],
    note: ''
  });

  useEffect(() => {
    if (motoList.length > 0) {
      loadModifiche();
    } else {
      setLoading(false);
    }
  }, [selectedMotoId, motoList]);

  const loadModifiche = async () => {
    try {
      setLoading(true);
      const response = await modificheApi.getAll(selectedMotoId || undefined);
      setModifiche(response.data);
    } catch (error) {
      console.error('Error loading modifiche:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    const today = new Date();
    const formattedDate = `${String(today.getDate()).padStart(2, '0')}.${String(today.getMonth() + 1).padStart(2, '0')}.${today.getFullYear()}`;
    setFormData({
      moto_id: selectedMotoId || motoList[0]?.id || '',
      titolo: '',
      descrizione: '',
      categoria: '',
      costo: null,
      data: formattedDate,
      foto_urls: [],
      note: ''
    });
    setEditingId(null);
  };

  const openLightbox = (images, index = 0) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const getImageUrl = (url) => {
    return url?.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
  };

  const handleOpenDialog = (modifica = null) => {
    if (modifica) {
      setFormData({
        moto_id: modifica.moto_id,
        titolo: modifica.titolo,
        descrizione: modifica.descrizione,
        categoria: modifica.categoria,
        costo: modifica.costo,
        data: modifica.data,
        foto_urls: modifica.foto_urls || [],
        note: modifica.note || ''
      });
      setEditingId(modifica.id);
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.moto_id || !formData.titolo || !formData.descrizione || !formData.categoria || !formData.data) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await modificheApi.update(editingId, formData);
        toast.success('Modifica aggiornata');
      } else {
        await modificheApi.create(formData);
        toast.success('Modifica registrata');
      }
      setDialogOpen(false);
      loadModifiche();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa modifica?')) return;
    
    try {
      await modificheApi.delete(id);
      toast.success('Modifica eliminata');
      loadModifiche();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '—';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'CHF'
    }).format(value);
  };

  const getMotoName = (motoId) => {
    const moto = motoList.find(m => m.id === motoId);
    return moto ? `${moto.marca} ${moto.modello}` : '—';
  };

  if (loading) {
    return (
      <Layout>
        <div className="page-container flex items-center justify-center min-h-[60vh]">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container" data-testid="modifiche-page">
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Modifiche</h1>
            <p className="page-subtitle">Personalizzazioni e upgrade</p>
          </div>
          
          <div className="flex gap-3">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  onClick={() => handleOpenDialog()}
                  disabled={motoList.length === 0}
                  className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm h-12 px-6"
                  data-testid="add-modifica-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Aggiungi
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-['Teko'] text-2xl text-white uppercase">
                    {editingId ? 'Modifica Voce' : 'Nuova Modifica'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label className="form-label">Moto *</Label>
                    <Select 
                      value={formData.moto_id} 
                      onValueChange={(v) => setFormData({...formData, moto_id: v})}
                    >
                      <SelectTrigger className="bg-black/50 border-[#262626] rounded-none h-11" data-testid="mod-moto-select">
                        <SelectValue placeholder="Seleziona moto" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                        {motoList.map(m => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.marca} {m.modello}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="form-label">Titolo *</Label>
                    <Input
                      value={formData.titolo}
                      onChange={(e) => setFormData({...formData, titolo: e.target.value})}
                      placeholder="es. Scarico Akrapovic"
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                      data-testid="mod-titolo"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="form-label">Categoria *</Label>
                      <Select 
                        value={formData.categoria} 
                        onValueChange={(v) => setFormData({...formData, categoria: v})}
                      >
                        <SelectTrigger className="bg-black/50 border-[#262626] rounded-none h-11" data-testid="mod-categoria-select">
                          <SelectValue placeholder="Categoria" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                          {CATEGORIE_MODIFICHE.map(c => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="form-label">Data *</Label>
                      <Input
                        value={formData.data}
                        onChange={(e) => setFormData({...formData, data: e.target.value})}
                        placeholder="GG.MM.AAAA"
                        className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                        data-testid="mod-data"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="form-label">Descrizione *</Label>
                    <Textarea
                      value={formData.descrizione}
                      onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                      placeholder="Descrivi la modifica..."
                      rows={3}
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none resize-none"
                      data-testid="mod-descrizione"
                    />
                  </div>

                  <div>
                    <Label className="form-label">Costo (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.costo || ''}
                      onChange={(e) => setFormData({...formData, costo: e.target.value ? parseFloat(e.target.value) : null})}
                      onFocus={(e) => e.target.select()}
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                      data-testid="mod-costo"
                    />
                  </div>

                  {/* Foto Upload */}
                  <div>
                    <Label className="form-label">Foto</Label>
                    <FotoUploader
                      fotoUrls={formData.foto_urls}
                      setFotoUrls={(urls) => {
                        if (typeof urls === 'function') {
                          setFormData(prev => ({...prev, foto_urls: urls(prev.foto_urls)}));
                        } else {
                          setFormData({...formData, foto_urls: urls});
                        }
                      }}
                      maxPhotos={5}
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                      className="flex-1 h-11 border-[#262626] text-white hover:bg-white/5 uppercase tracking-wider rounded-sm"
                    >
                      Annulla
                    </Button>
                    <Button
                      type="submit"
                      disabled={saving}
                      className="flex-1 h-11 bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm"
                      data-testid="mod-submit"
                    >
                      {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : (editingId ? 'Aggiorna' : 'Salva')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {motoList.length === 0 ? (
          <div className="vault-card text-center py-16">
            <Settings2 className="w-16 h-16 text-[#4B5563] mx-auto mb-4" />
            <h3 className="text-white text-xl mb-2">Prima aggiungi una moto</h3>
            <p className="text-[#9CA3AF]">Devi avere almeno una moto per registrare le modifiche</p>
          </div>
        ) : modifiche.length === 0 ? (
          <div className="vault-card text-center py-16">
            <Settings2 className="w-16 h-16 text-[#4B5563] mx-auto mb-4" />
            <h3 className="text-white text-xl mb-2">Nessuna modifica registrata</h3>
            <p className="text-[#9CA3AF] mb-6">Inizia a tracciare le personalizzazioni della tua moto</p>
            <Button 
              onClick={() => handleOpenDialog()}
              className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm h-11 px-6"
            >
              <Plus className="w-5 h-5 mr-2" />
              Aggiungi Modifica
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {modifiche.map((mod) => (
              <div
                key={mod.id}
                className="vault-card"
                data-testid={`modifica-item-${mod.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FF5F1F]/10 flex items-center justify-center">
                      <Settings2 className="w-5 h-5 text-[#FF5F1F]" />
                    </div>
                    <div>
                      <span className="vault-badge vault-badge-orange">{mod.categoria}</span>
                      <p className="text-[#9CA3AF] text-xs mt-1">{getMotoName(mod.moto_id)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleOpenDialog(mod)}
                      className="p-2 text-[#9CA3AF] hover:text-[#FF5F1F] transition-colors"
                      data-testid={`edit-mod-${mod.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(mod.id)}
                      className="p-2 text-[#9CA3AF] hover:text-red-500 transition-colors"
                      data-testid={`delete-mod-${mod.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <h3 className="font-['Teko'] text-xl text-white uppercase mb-2">{mod.titolo}</h3>
                <p className="text-[#9CA3AF] text-sm mb-4">{mod.descrizione}</p>

                {mod.foto_urls && mod.foto_urls.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {mod.foto_urls.slice(0, 3).map((url, idx) => (
                      <div 
                        key={idx} 
                        className="relative cursor-pointer group/img"
                        onClick={() => openLightbox(mod.foto_urls, idx)}
                      >
                        <img 
                          src={getImageUrl(url)} 
                          alt="" 
                          className="w-full h-16 object-cover border border-[#262626]" 
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                          <ZoomIn className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-[#262626]">
                  <span className="text-[#FF5F1F] font-mono">{formatCurrency(mod.costo)}</span>
                  <span className="text-[#9CA3AF] font-mono text-sm">{mod.data}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ImageLightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </Layout>
  );
}
